<template>
  <div class="ecom-table-container flex-grow-1">
    <v-data-table
      :headers="headers"
      :items="mappedItems"
      class="text-sm table-radius border border-[#e0e0e0]"
      :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30, 50] }"
      :options.sync="options"
      :server-items-length="totalItems"
      :loading="loading"
      fixed-header
      show-select
      show-expand
      v-model="selected"
      item-key="_id"
      disable-sort
      single-expand
    >
      <!-- Header rendering similar to CaseTable -->
      <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
        <p
          v-if="header.value !== 'data-table-select' && header.value !== 'data-table-expand'"
          :key="index"
          class="font-title"
        >
          {{ header.text }}
        </p>
      </template>

      <template v-slot:item.customerName="{ item }">
        <td class="px-4 py-3 table-font">
          <div v-if="activeEditingCaseId === item._id" class="min-w-[200px]">
            <v-menu
              v-model="showCustomerMenu"
              offset-y
              max-height="300"
              :close-on-content-click="false"
              min-width="260"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="customerSearchInput"
                  dense
                  outlined
                  hide-details
                  autofocus
                  placeholder="Search by Name, CRN or Case No"
                  :loading="loadingCustomers"
                  v-bind="attrs"
                  v-on="on"
                  @input="debounceSearchCustomer"
                  append-icon="mdi-close"
                  @click:append="activeEditingCaseId = null"
                ></v-text-field>
              </template>
              <v-card class="rounded-md shadow-lg border">
                <v-list v-if="customersSearchList.length > 0" dense class="py-0">
                  <v-list-item
                    v-for="opt in customersSearchList"
                    :key="opt.optionId"
                    @click="updateCaseCustomer(item, opt)"
                    class="hover:bg-blue-50 cursor-pointer"
                  >
                    <v-list-item-content>
                      <v-list-item-title class="font-bold text-sm">
                        {{ opt.customerName }}
                      </v-list-item-title>
                      <v-list-item-subtitle v-if="opt.caseNo" class="text-caption grey--text">
                        Case No: {{ opt.caseNo }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
                <div v-else class="px-4 py-3 text-caption grey--text text-center">
                  {{ loadingCustomers ? 'Searching...' : 'Type to search customer...' }}
                </div>
              </v-card>
            </v-menu>
          </div>
          <div
            v-else-if="
              !item.customer ||
              (Array.isArray(item.customer) && item.customer.length === 0) ||
              !item.customer._id
            "
            class="cursor-pointer text-blue-600 font-semibold hover:text-blue-800 transition-all duration-200"
            @click="startEditingCustomer(item)"
          >
            Link Customer
            <v-icon small class="ml-1 text-gray-400">mdi-pencil</v-icon>
          </div>
          <div v-else class="d-flex align-center">
            <span class="font-semibold text-gray-800">{{ item.customerName }}</span>
            <v-btn
              icon
              small
              class="ml-1 text-gray-400 hover:text-blue-600"
              @click="startEditingCustomer(item)"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </div>
        </td>
      </template>

      <template v-slot:item.customerEmail="{ item }">
        <td class="px-4 py-3 table-font">
          <span class="text-gray-600">{{ item.customerEmail }}</span>
        </td>
      </template>

      <template v-slot:item.totalCases="{ item }">
        <td class="px-4 py-3 table-font font-weight-bold">
          {{ item.totalCases || 0 }}
        </td>
      </template>

      <template v-slot:item.openCases="{ item }">
        <td class="px-4 py-3 table-font font-weight-bold text-blue-600">
          {{ item.openCases || 0 }}
        </td>
      </template>

      <template v-slot:item.inProgressCases="{ item }">
        <td class="px-4 py-3 table-font font-weight-bold orange--text text--darken-3">
          {{ item.inProgressCases || 0 }}
        </td>
      </template>

      <template v-slot:item.closedCases="{ item }">
        <td class="px-4 py-3 table-font font-weight-bold success--text">
          {{ item.closedCases || 0 }}
        </td>
      </template>

      <template v-slot:item.data-table-expand="{ isExpanded, expand, item }">
        <v-btn v-if="item.isGroup" icon small @click="toggleExpand(item, isExpanded, expand)">
          <v-icon>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </template>

      <template v-slot:expanded-item="{ headers: tableHeaders, item }">
        <td :colspan="tableHeaders.length" class="bg-gray-50 p-4">
          <v-card flat outlined class="">
            <v-card-title
              class="text-subtitle-2 font-weight-bold grey--text text--darken-3 pb-2 pt-2"
            >
              Complaints for {{ item.customerName }}
            </v-card-title>
            <div style="max-height: 350px; overflow-y: auto; overflow-x: auto">
              <v-data-table
                :key="'nested_table_' + item._id"
                :headers="nestedHeaders"
                :items="nestedCasesMap[item._id] || []"
                :server-items-length="nestedTotalMap[item._id] || 0"
                :loading="nestedLoadingMap[item._id]"
                :options="nestedOptionsMap[item._id]"
                @update:options="(newOpts) => handleNestedOptionsUpdate(item._id, newOpts)"
                :footer-props="{ itemsPerPageOptions: [5, 10, 20] }"
                dense
                class="elevation-0"
                disable-sort
              >
                <template v-slot:item.caseNo="{ item: childItem }">
                  <td
                    class="px-4 py-3 table-font font-weight-bold primary--text cursor-pointer hover:underline text-subtitle-2"
                    @click="onCaseClick(childItem)"
                  >
                    {{ childItem.caseNo }}
                  </td>
                </template>
                <template v-slot:item.assignedTo="{ item: childItem }">
                  <td class="px-4 py-3 table-font">
                    <v-autocomplete
                      v-model="childItem.assignedTo"
                      :items="users"
                      item-text="fullName"
                      item-value="_id"
                      class="input-field dense-autocomplete"
                      label="Assigned To"
                      placeholder="Assigned To"
                      hide-details
                      flat
                      solo
                      multiple
                      small-chips
                      deletable-chips
                      @change="(val) => handleAssignedToChange(val, childItem, item._id)"
                    ></v-autocomplete>
                  </td>
                </template>
                <template v-slot:item.status="{ item: childItem }">
                  <td class="px-4 py-3 table-font">
                    <div class="d-flex flex-column" style="width: fit-content">
                      <OrderStatuses
                        :orderStatus="childItem.status"
                        :statuses="caseStatusOptions"
                        @statusChange="
                          (status) => caseStatusUpdate(status, childItem, 'status', item._id)
                        "
                      />
                    </div>
                  </td>
                </template>
                <template v-slot:item.additionalColumns.attachment="{ item: childItem }">
                  <td class="px-4 py-3 table-font">
                    <span
                      v-if="
                        childItem.additionalColumns &&
                        childItem.additionalColumns.attachment &&
                        childItem.additionalColumns.attachment.length > 0
                      "
                      class="truncate-attachment"
                      :title="formatAttachments(childItem.additionalColumns.attachment)"
                    >
                      {{ formatAttachments(childItem.additionalColumns.attachment) }}
                    </span>
                    <span v-else>-</span>
                  </td>
                </template>
                <template v-slot:item.createdAt="{ item: childItem }">
                  <td class="px-4 py-3 table-font">
                    {{ formatDateTime(childItem.createdAt) }}
                  </td>
                </template>
                <template v-slot:item.action="{ item: childItem }">
                  <td class="px-4 py-3 table-font">
                    <action-button
                      :item="childItem"
                      :canEdit="canEdit"
                      :canWrite="canWrite"
                      :canDelete="canDelete"
                      :handleEdit="() => $emit('edit-case', childItem)"
                      :addTaskAction="() => $emit('add-task', childItem)"
                      :handleDelete="() => $emit('delete-case', childItem)"
                      :showAddIcon="'mdi-plus'"
                      :plusIconText="`Add ${tabText?.visitTable?.displayName || 'Visit'}`"
                      :showEditButton="canEdit"
                      :showAddTaskButton="canWrite && !isLoanManagement"
                      :showDeleteButton="canDelete"
                    />
                  </td>
                </template>
              </v-data-table>
            </div>
          </v-card>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import OrderStatuses from '../../InventoryManagement/OrderStatuses.vue'
import ActionButton from '@/components/ActionButton'

export default {
  name: 'EmailTimelineTable',
  components: {
    OrderStatuses,
    ActionButton
  },
  props: {
    cases: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    totalItems: {
      type: Number,
      default: 0
    },
    users: {
      type: Array,
      default: () => []
    },
    caseConfig: {
      type: Object,
      default: () => ({})
    },
    subCases: {
      type: Array,
      default: () => []
    },
    caseStatusOptions: {
      type: Array,
      default: () => []
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    canWrite: {
      type: Boolean,
      default: false
    },
    canDelete: {
      type: Boolean,
      default: false
    },
    isLoanManagement: {
      type: Boolean,
      default: false
    },
    tabText: {
      type: Object,
      default: () => ({})
    },
    filterParams: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      options: {
        page: 1,
        itemsPerPage: 10
      },
      activeEditingCaseId: null,
      showCustomerMenu: false,
      customerSearchInput: '',
      customersSearchList: [],
      loadingCustomers: false,
      searchTimer: null,
      nestedCasesMap: {},
      nestedOptionsMap: {},
      nestedTotalMap: {},
      nestedLoadingMap: {},
      headers: [
        { text: '', value: 'data-table-expand', sortable: false },
        { text: '', value: 'data-table-select', sortable: false },
        { text: 'Customer', value: 'customerName' },
        { text: 'Sender Email', value: 'customerEmail' },
        { text: 'Total', value: 'totalCases' },
        { text: 'Open', value: 'openCases' },
        { text: 'In Progress', value: 'inProgressCases' },
        { text: 'Closed', value: 'closedCases' }
      ]
    }
  },
  computed: {
    selected: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    nestedHeaders() {
      const headers = [{ text: 'Case No / Complaint', value: 'caseNo', align: 'start' }]
      if (this.caseConfig?.additionalColumns) {
        Object.entries(this.caseConfig.additionalColumns).forEach(([key, col]) => {
          if (col.tableVisibility !== false) {
            headers.push({
              text: col.displayName,
              value: `additionalColumns.${key}`,
              align: 'start'
            })
          }
        })
      }
      headers.push(
        { text: 'Assigned To', value: 'assignedTo', align: 'start', width: '220px' },
        { text: 'Status', value: 'status', align: 'start', width: '150px' },
        { text: 'Created At', value: 'createdAt', align: 'start' },
        { text: 'Action', value: 'action', align: 'start', width: '150px' }
      )
      return headers
    },
    mappedItems() {
      return this.cases.map((item) => {
        let timelineInfo = ''
        if (item.isGroup) {
          const parts = [
            `Total: ${item.totalCases || 0}`,
            `Open: ${item.openCases || 0}`,
            `In Progress: ${item.inProgressCases || 0}`,
            `Closed: ${item.closedCases || 0}`
          ]
          timelineInfo = parts.join(' | ')
        } else {
          timelineInfo = item.caseNo
        }
        return {
          ...item,
          customerName: item.customer?.customerFullName || item.customer?.name || 'N/A (Unlinked)',
          customerEmail: item.additionalColumns?.sender || item.customer?.customerEmail || 'N/A',
          info: timelineInfo
        }
      })
    }
  },
  watch: {
    options: {
      handler(newVal) {
        this.$emit('update:options', newVal)
      },
      deep: true
    }
  },
  methods: {
    onCaseClick(caseItem) {
      this.$emit('case-details', caseItem)
    },
    toggleExpand(item, isExpanded, expand) {
      expand(!isExpanded)
      if (!isExpanded) {
        if (!this.nestedOptionsMap[item._id]) {
          this.$set(this.nestedOptionsMap, item._id, { page: 1, itemsPerPage: 5 })
        }
      }
    },
    handleNestedOptionsUpdate(groupId, newOpts) {
      this.$set(this.nestedOptionsMap, groupId, {
        ...this.nestedOptionsMap[groupId],
        ...newOpts
      })
      this.fetchNestedCases(groupId)
    },
    async fetchNestedCases(groupId) {
      if (!groupId) return
      this.$set(this.nestedLoadingMap, groupId, true)
      try {
        const opts = this.nestedOptionsMap[groupId] || { page: 1, itemsPerPage: 5 }
        const params = {
          page: opts.page,
          limit: opts.itemsPerPage,
          ...this.filterParams
        }
        const response = await this.$axios.get(`/collection/groups/${groupId}/cases`, { params })
        const cases = response.data?.cases || []
        this.$set(this.nestedCasesMap, groupId, cases)
        this.$set(
          this.nestedTotalMap,
          groupId,
          response.data?.pagination?.totalCount || cases.length
        )
      } catch (error) {
        console.error(error)
        this.$toast.error('Failed to load complaints')
      } finally {
        this.$set(this.nestedLoadingMap, groupId, false)
      }
    },
    startEditingCustomer(item) {
      this.activeEditingCaseId = item._id
      this.customerSearchInput = ''
      this.customersSearchList = []
      this.showCustomerMenu = true
    },
    debounceSearchCustomer(val) {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.searchCustomerForCase(val)
      }, 500)
    },
    async searchCustomerForCase(val) {
      if (!val || typeof val !== 'string' || !val.trim()) return
      this.loadingCustomers = true
      this.showCustomerMenu = true
      try {
        const linkedSubCase = (this.subCases || []).find(
          (s) => s._id === this.caseConfig?.crossLinkedSubCaseId
        )
        const response = await this.$axios.get('/customers/search-linked', {
          params: {
            search: val,
            useCase: linkedSubCase?.useCase || 'loan_management'
          }
        })
        const rawList = response.data.data || response.data.customers || []
        const flattenedOptions = []
        rawList.forEach((cust) => {
          const custName = cust.name || cust.customerFullName || 'Customer'
          if (cust.cases && cust.cases.length > 0) {
            cust.cases.forEach((c) => {
              flattenedOptions.push({
                ...cust,
                optionId: `${cust._id}_${c._id}`,
                customerId: cust._id,
                caseId: c._id,
                caseNo: c.caseNo,
                customerName: custName
              })
            })
          } else {
            flattenedOptions.push({
              ...cust,
              optionId: `${cust._id}_no_case`,
              customerId: cust._id,
              caseId: null,
              caseNo: null,
              customerName: custName
            })
          }
        })
        this.customersSearchList = flattenedOptions
      } catch (error) {
        console.error(error)
      } finally {
        this.loadingCustomers = false
      }
    },
    async updateCaseCustomer(item, selectedOption) {
      if (!selectedOption || !selectedOption.customerId) return
      try {
        const payload = {
          customer: selectedOption.customerId
        }
        if (selectedOption.caseId) {
          payload.cross_case_linking = [selectedOption.caseId]
        }

        let caseId = item._id
        if (caseId && typeof caseId === 'string' && caseId.startsWith('group_')) {
          const nested = this.nestedCasesMap[item._id] || []
          if (nested.length > 0) {
            caseId = nested[0]._id
          } else {
            const response = await this.$axios.get(`/collection/groups/${item._id}/cases`, {
              params: {
                page: 1,
                limit: 1,
                ...this.filterParams
              }
            })
            const cases = response.data?.cases || []
            if (cases.length > 0) {
              caseId = cases[0]._id
            }
          }
        }

        await this.$axios.put(`/collection/case/${caseId}`, payload, {
          params: { updateEmail: true }
        })
        this.$toast.success('Customer linked successfully')
        this.activeEditingCaseId = null
        this.showCustomerMenu = false
        this.$emit('refresh')
      } catch (error) {
        console.error(error)
        this.$toast.error(error.response?.data?.message || 'Failed to link customer')
      }
    },
    handleAssignedToChange(val, item, parentId) {
      const next = val || []
      this.$set(item, 'assignedTo', next)
      this.caseStatusUpdate(next, item, 'assignedTo', parentId)
    },
    async caseStatusUpdate(newValue, item, field, parentId) {
      try {
        const payload = {}
        if (field === 'status') {
          payload.status = newValue
        } else if (field === 'assignedTo') {
          payload.assignedTo = newValue
        }
        await this.$axios.put(`/collection/cases/${item._id}`, payload)
        this.$toast.success('Case updated successfully')
        if (parentId) {
          this.fetchNestedCases(parentId)
        }
        this.$emit('refresh')
      } catch (error) {
        console.error(error)
        this.$toast.error(error.response?.data?.message || 'Failed to update case')
      }
    },
    formatDateTime(date) {
      if (!date) return 'N/A'
      try {
        const d = new Date(date)
        const dateStr = d
          .toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })
          .replace(/\//g, '-')
        const timeStr = d.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        })
        return `${dateStr} ${timeStr}`
      } catch (e) {
        return date
      }
    },
    formatAttachments(attachments) {
      if (!attachments || !Array.isArray(attachments)) return '-'
      return attachments
        .map((att) => att.filename || att.name || 'Attachment')
        .filter(Boolean)
        .join(', ')
    }
  }
}
</script>

<style scoped>
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dense-autocomplete >>> .v-input__control {
  min-height: 32px !important;
}
.elevation-0 >>> tbody > tr > td {
  padding: 12px 16px !important;
  font-size: 0.875rem !important;
}
.truncate-attachment {
  display: inline-block;
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
</style>
